/**
 * Question Type Normalization Utility
 * Single source of truth for question type handling across ExamLock frontend
 *
 * IMPORTANT: This must stay synchronized with functions/src/questionTypeUtils.js
 */

// Canonical question types - use hyphens for consistency with React component conventions
export const CANONICAL_TYPES = {
  MULTIPLE_CHOICE: 'multiple-choice',
  TRUE_FALSE: 'true-false',
  SHORT_ANSWER: 'short-answer',
  FILL_BLANK: 'fill-blank',
  ESSAY: 'essay',
  MATCHING: 'matching',
  MULTIPLE_SELECT: 'multiple-select',
  CLOZE: 'cloze'
} as const;

// Type aliases for backward compatibility
const TYPE_ALIASES: Record<string, string> = {
  // MCQ aliases
  'mcq': CANONICAL_TYPES.MULTIPLE_CHOICE,
  'multiple_choice': CANONICAL_TYPES.MULTIPLE_CHOICE,
  'multiplechoice': CANONICAL_TYPES.MULTIPLE_CHOICE,

  // True/False aliases
  'truefalse': CANONICAL_TYPES.TRUE_FALSE,
  'true_false': CANONICAL_TYPES.TRUE_FALSE,
  'tf': CANONICAL_TYPES.TRUE_FALSE,

  // Short answer aliases
  'short': CANONICAL_TYPES.SHORT_ANSWER,
  'short_answer': CANONICAL_TYPES.SHORT_ANSWER,
  'shortanswer': CANONICAL_TYPES.SHORT_ANSWER,
  'text': CANONICAL_TYPES.SHORT_ANSWER,

  // Fill blank aliases
  'fillblank': CANONICAL_TYPES.FILL_BLANK,
  'fill_blank': CANONICAL_TYPES.FILL_BLANK,
  'fill-in-the-blank': CANONICAL_TYPES.FILL_BLANK,
  'fitb': CANONICAL_TYPES.FILL_BLANK,

  // Essay aliases
  'long-answer': CANONICAL_TYPES.ESSAY,
  'paragraph': CANONICAL_TYPES.ESSAY,
  'essay': CANONICAL_TYPES.ESSAY,

  // Matching aliases
  'match': CANONICAL_TYPES.MATCHING,
  'matching-pairs': CANONICAL_TYPES.MATCHING,

  // Multiple select aliases
  'multipleselect': CANONICAL_TYPES.MULTIPLE_SELECT,
  'multiple_select': CANONICAL_TYPES.MULTIPLE_SELECT,
  'checkbox': CANONICAL_TYPES.MULTIPLE_SELECT,
  'select-all': CANONICAL_TYPES.MULTIPLE_SELECT,

  // Cloze aliases
  'cloze_passage': CANONICAL_TYPES.CLOZE,
  'cloze-passage': CANONICAL_TYPES.CLOZE,
  'passage': CANONICAL_TYPES.CLOZE
};

/**
 * Normalize a question type to canonical form
 * @param type - Raw question type string
 * @returns Canonical type or null if invalid
 */
export function normalizeQuestionType(type: string | undefined | null): string | null {
  if (!type || typeof type !== 'string') return null;

  const normalized = type.toLowerCase().trim();

  // Check if already canonical
  if (Object.values(CANONICAL_TYPES).includes(normalized as any)) {
    return normalized;
  }

  // Check aliases
  if (TYPE_ALIASES[normalized]) {
    return TYPE_ALIASES[normalized];
  }

  return null;
}

/**
 * Get display name for a question type
 */
export function getQuestionTypeDisplayName(type: string): string {
  const canonical = normalizeQuestionType(type);

  const displayNames: Record<string, string> = {
    [CANONICAL_TYPES.MULTIPLE_CHOICE]: 'Multiple Choice',
    [CANONICAL_TYPES.TRUE_FALSE]: 'True/False',
    [CANONICAL_TYPES.SHORT_ANSWER]: 'Short Answer',
    [CANONICAL_TYPES.FILL_BLANK]: 'Fill in the Blank',
    [CANONICAL_TYPES.ESSAY]: 'Essay',
    [CANONICAL_TYPES.MATCHING]: 'Matching',
    [CANONICAL_TYPES.MULTIPLE_SELECT]: 'Multiple Select',
    [CANONICAL_TYPES.CLOZE]: 'Cloze Passage'
  };

  return canonical ? displayNames[canonical] || 'Unknown' : 'Unknown';
}

/**
 * Check if a question type requires manual grading
 */
export function requiresManualGrading(type: string): boolean {
  const canonical = normalizeQuestionType(type);

  // Only short answer and essay require manual grading
  return canonical === CANONICAL_TYPES.SHORT_ANSWER ||
         canonical === CANONICAL_TYPES.ESSAY;
}

/**
 * Check if a question type is auto-graded
 */
export function isAutoGraded(type: string): boolean {
  return !requiresManualGrading(type);
}
